import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * The PUBLIC capability-status contract.
 *
 * ## Server-barrel only, deliberately
 *
 * This contract is exported from `@nexus/types/server` and NOT from the client
 * barrel's value surface, and it is absent from `packages/api-codegen/config.ts`,
 * so no frontend client is generated for it. `ZAdminPipedreamReconciliation` is
 * the existing precedent and `nexus-api-contract-table.ts` records why the shape
 * is legitimate: the contract table reads BOTH barrels, so a server-only
 * descriptor still binds its handler's return type.
 *
 * The consumer here is an external status page holding no credential, not the
 * Nexus frontend — generating a React hook nobody calls would be noise.
 *
 * ## The `{ success, data }` envelope, and what it means for an outside reader
 *
 * Every internal NexusApi endpoint wraps its payload, and
 * `nexus/require-api-success-wrapper` holds that at zero errors for any handler
 * with a `TApi` return type — so binding the contract and serving a bare body are
 * not both available. Consistency wins: one response shape across the API is worth
 * more than saving an external consumer one field access.
 *
 * 🔑 SO THE VERSION GATE IS `body.data.apiVersion`, NOT `body.apiVersion`. A reader
 * must unwrap first, and `apiVersion` is still the first thing it reads inside.
 */

const READINESS = ["ready", "degraded", "unavailable"] as const;

export const CapabilityReadinessSchema = z.enum(READINESS);

export const PublicCapabilityStatusSchema = z.object({
  /** Stable machine name for the FEATURE. Never a class name, never a variable. */
  name: z.string(),
  status: CapabilityReadinessSchema,
  /**
   * The effect, never the mechanism. The backend's `CapabilityRegistry` refuses a
   * reason naming a declared config key or any variable-shaped token, and redacts
   * one that begins leaking after registration.
   */
  reason: z.string()
});

export const CapabilityStatusPayloadSchema = z.object({
  /**
   * Gate on this BEFORE reading any other field. Bumped only when a reader that
   * understood the old shape would MISREAD the new one; a new optional field does
   * not bump it.
   */
  apiVersion: z.number().int().positive(),

  /**
   * 🔴 A CONFIGURATION READING, NEVER A LIVENESS PROBE. `ready` means this process
   * resolved the capability's configuration — not that the dependency behind it is
   * reachable. It is also not uniformly current: at least one capability decides
   * in its constructor and never re-reads, and a pod's environment is itself a
   * snapshot taken at container start.
   */
  basis: z.literal("process-configuration"),

  /** Counts over the LISTED capabilities only — never the registry's true total. */
  summary: z.object({
    total: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative()
  }),

  capabilities: z.array(PublicCapabilityStatusSchema)
});

export const ZCapabilityStatus = {
  /**
   * GET /api/capabilities — unauthenticated. The consumer is a status page.
   */
  Read: {
    method: "GET" as const,
    path: "/api/capabilities",
    Response: ApiSuccessSchema(CapabilityStatusPayloadSchema)
  }
} as const;
